import React, { useEffect, useState, useCallback } from "react";
import { PanelHelp } from "./QuickStart";
import { toast } from "sonner";
import {
  getTickets,
  deleteTicket,
  clearTickets,
  updateTicket,
  saveTicket,
  onTicketHistoryChange,
} from "../lib/ticketHistory";
import { scoreTicket } from "../lib/scoreTicket";
import { todayLocalIso } from "../lib/dateHelpers";
import { SaddleCloth } from "./SaddleCloth";
import { History, Trash2, X, Trophy, Loader2, CheckCircle2, XCircle, Plus, Pencil, Save } from "lucide-react";

const fmtTime = (ts) => {
  try {
    const d = new Date(ts);
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const ScoreBadge = ({ score }) => {
  if (!score) return null;
  const bg = score.hit ? "#84cc16" : "#7f1d1d";
  const fg = score.hit ? "#0a0a0a" : "#fecaca";
  const Icon = score.hit ? CheckCircle2 : XCircle;
  const label = score.hit ? "HIT" : "MISS";
  const finishers = Object.entries(score.finishers || {})
    .map(([k, v]) => `${k}:${v}`)
    .join(" · ");
  const money =
    score.payoff != null
      ? ` · pays $${score.payoff.toFixed(2)}`
      : "";
  const net =
    score.net != null
      ? ` · net ${score.net >= 0 ? "+" : "-"}$${Math.abs(score.net).toFixed(2)}`
      : "";
  return (
    <span
      data-testid="hist-score-badge"
      data-hit={score.hit ? "true" : "false"}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-[0.18em]"
      style={{
        background: bg,
        color: fg,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      }}
      title={`${finishers}${money}${net}`}
    >
      <Icon size={12} />
      {label} · {finishers}
      {money}
      {net}
    </span>
  );
};

// Inline edit form for an archived ticket. Only lets the user fix the
// metadata that matters for scoring: track, race, date, base. Posts are
// locked (delete + manual-add if you need to change them).
const EditTicketInline = ({ t, tracks, onCancel, onSaved }) => {
  const [trackSlug, setTrackSlug] = useState(t.track_slug || "");
  const [race, setRace] = useState(t.race != null ? String(t.race) : "");
  const [date, setDate] = useState(t.date || todayLocalIso());
  const [base, setBase] = useState(String(t.base ?? 1));

  const inputCls =
    "px-2 h-8 rounded bg-[#0a0a0a] text-stone-100 text-xs border border-stone-700 focus:border-[#84cc16] focus:outline-none placeholder-stone-600";
  const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1";
  const monoStyle = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

  const save = () => {
    const baseNum = Number(base);
    if (!race) {
      toast.error("Race is required.");
      return;
    }
    if (!baseNum || baseNum <= 0) {
      toast.error("Base must be greater than 0.");
      return;
    }
    const selected = tracks.find((x) => x.slug === trackSlug);
    const trackName = selected?.name || t.track || trackSlug || "";
    const combos = Number(t.combos) || 0;
    const newCost = (combos * baseNum).toFixed(2);
    const patch = {
      track: trackName,
      track_slug: trackSlug,
      race: String(race),
      date,
      base: baseNum,
      cost: newCost,
      // Any prior score is invalidated when the race/track changes — drop it
      // so the user re-scores against the correct result.
      score: null,
    };
    updateTicket(t.id, patch);
    toast.success(
      `Ticket updated — ${trackName || "unknown"} · Race ${race}`,
      { duration: 3500 }
    );
    onSaved?.({ ...t, ...patch });
  };

  return (
    <div
      data-testid={`edit-${t.id}`}
      className="grid gap-3 md:grid-cols-5 bg-[#0a0a0a] border border-stone-800 rounded p-3"
    >
      <div className="md:col-span-2">
        <label className={labelCls} style={monoStyle}>Track</label>
        <select
          data-testid={`edit-track-${t.id}`}
          value={trackSlug}
          onChange={(e) => setTrackSlug(e.target.value)}
          className={inputCls}
          style={{ ...monoStyle, width: "100%" }}
        >
          {!tracks.find((x) => x.slug === trackSlug) && (
            <option value={trackSlug}>{t.track || trackSlug || "—"}</option>
          )}
          {tracks.map((x) => (
            <option key={x.slug} value={x.slug}>
              {x.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls} style={monoStyle}>Race</label>
        <input
          data-testid={`edit-race-${t.id}`}
          value={race}
          onChange={(e) => setRace(e.target.value.replace(/[^\d]/g, ""))}
          className={inputCls}
          style={{ ...monoStyle, width: "100%" }}
        />
      </div>
      <div>
        <label className={labelCls} style={monoStyle}>Date</label>
        <input
          data-testid={`edit-date-${t.id}`}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={inputCls}
          style={{ ...monoStyle, width: "100%" }}
        />
      </div>
      <div>
        <label className={labelCls} style={monoStyle}>Base $</label>
        <input
          data-testid={`edit-base-${t.id}`}
          value={base}
          onChange={(e) => setBase(e.target.value.replace(/[^\d.]/g, ""))}
          className={inputCls}
          style={{ ...monoStyle, width: "100%" }}
        />
      </div>
      <div className="md:col-span-5 flex items-center justify-end gap-2">
        <button
          data-testid={`edit-cancel-${t.id}`}
          onClick={onCancel}
          className="px-3 h-8 rounded text-[10px] uppercase tracking-[0.18em] text-stone-400 hover:text-stone-200 border border-stone-700"
          style={monoStyle}
        >
          Cancel
        </button>
        <button
          data-testid={`edit-save-${t.id}`}
          onClick={save}
          className="inline-flex items-center gap-1 px-3 h-8 rounded text-[10px] font-bold uppercase tracking-[0.18em] bg-[#84cc16] text-[#0a0a0a] border border-[#365314] hover:bg-[#a3e635]"
          style={monoStyle}
        >
          <Save size={11} /> Save
        </button>
      </div>
    </div>
  );
};

const TicketRow = ({ t, onDelete, onScore, scoring, tracks }) => {
  const [editing, setEditing] = useState(false);
  return (
  <div
    data-testid={`hist-${t.id}`}
    className="flex flex-col gap-2 px-4 py-3 border-b border-stone-800 last:border-b-0"
  >
    <div
      className="flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-stone-400"
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="px-1.5 py-0.5 rounded font-bold"
          style={{
            background:
              t.kind === "DD"
                ? "#84cc16"
                : t.kind === "Exacta"
                ? "#f5ecd6"
                : "#e7d7a8",
            color: "#0a0a0a",
          }}
        >
          {t.kind}
        </span>
        {t.track && <span className="text-[#f5ecd6]">{t.track}</span>}
        {t.race != null && <span>Race {t.race}</span>}
        <span className="text-stone-500">{fmtTime(t.ts)}</span>
        {t.score && <ScoreBadge score={t.score} />}
      </div>
      <div className="flex items-center gap-3">
        <span>
          <span className="text-stone-500">Combos </span>
          <span className="text-[#f5ecd6] font-bold">{t.combos}</span>
        </span>
        <span>
          <span className="text-stone-500">Cost </span>
          <span className="text-[#84cc16] font-bold">${t.cost}</span>
        </span>
        {!t.score && (
          <button
            onClick={() => onScore(t)}
            disabled={scoring || !t.track_slug || !t.race || !t.date}
            className="inline-flex items-center gap-1 px-2 h-6 rounded text-[10px] font-bold tracking-[0.18em] uppercase text-[#84cc16] border border-[#365314] hover:bg-[#84cc16] hover:text-[#0a0a0a] transition disabled:opacity-30"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            data-testid={`hist-score-${t.id}`}
            title={
              !t.track_slug
                ? "Ticket is missing track/date info — cannot score"
                : "Fetch W/P/S from Equibase-source and score this ticket"
            }
          >
            {scoring ? <Loader2 size={11} className="animate-spin" /> : <Trophy size={11} />}
            Score
          </button>
        )}
        <button
          onClick={() => setEditing((v) => !v)}
          className={`transition ${editing ? "text-[#84cc16]" : "text-stone-500 hover:text-[#f5ecd6]"}`}
          aria-label="Edit"
          data-testid={`hist-edit-${t.id}`}
          title="Edit track, race, date, or base amount"
        >
          <Pencil size={13} />
        </button>
        <button
          onClick={() => onDelete(t.id)}
          className="text-stone-500 hover:text-red-400 transition"
          aria-label="Delete"
          data-testid={`hist-delete-${t.id}`}
        >
          <X size={14} />
        </button>
      </div>
    </div>
    {editing && (
      <EditTicketInline
        t={t}
        tracks={tracks || []}
        onCancel={() => setEditing(false)}
        onSaved={() => setEditing(false)}
      />
    )}
    <div className="flex flex-col gap-1">
      {t.horses.map((row, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[10px] uppercase tracking-[0.18em] text-stone-500 w-28"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            {row.label}
          </span>
          {row.posts.map((n, j) => (
            <SaddleCloth key={`${n}-${j}`} n={n} />
          ))}
        </div>
      ))}
    </div>
  </div>
  );
};

// Parse a comma / space / dash separated post list → unique positive ints.
const parsePosts = (s) => {
  if (!s) return [];
  const seen = new Set();
  const out = [];
  for (const tok of String(s).split(/[^\d]+/)) {
    const n = parseInt(tok, 10);
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
};

const countExactaPairs = (front, back) => {
  let n = 0;
  for (const a of front) for (const b of back) if (a !== b) n += 1;
  return n;
};

// Ordered triples (a, b, c), one from each position, all different horses.
export const countTrifectaStraight = (first, second, third) => {
  let n = 0;
  for (const a of first)
    for (const b of second)
      if (b !== a) for (const c of third) if (c !== a && c !== b) n += 1;
  return n;
};

const PostsField = ({ label, ph, value, onChange, posts, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
      {label}
    </label>
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={ph}
      className="px-2 h-8 rounded bg-[#0a0a0a] text-stone-100 text-xs border border-stone-700 focus:border-[#84cc16] focus:outline-none placeholder-stone-600"
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", width: "100%" }}
    />
    {posts.length > 0 && (
      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {posts.map((n, i) => (
          <SaddleCloth key={`${n}-${i}`} n={n} />
        ))}
      </div>
    )}
  </div>
);

const ManualTicketForm = ({ tracks, onClose, onScoreTicket }) => {
  const [kind, setKind] = useState("Trifecta");
  const [trackSlug, setTrackSlug] = useState("");
  const [race, setRace] = useState("");
  const [date, setDate] = useState(() => todayLocalIso());
  const [base, setBase] = useState("0.50");
  // Field inputs — meaning varies by kind.
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [c, setC] = useState("");

  // Default the track dropdown once the list arrives.
  useEffect(() => {
    if (!trackSlug && tracks.length > 0) setTrackSlug(tracks[0].slug);
  }, [tracks, trackSlug]);

  // Default bases per kind.
  useEffect(() => {
    if (kind === "DD") setBase((prev) => (prev === "0.50" || prev === "2.00" ? "1.00" : prev));
    if (kind === "Exacta") setBase((prev) => (prev === "0.50" ? "1.00" : prev));
    if (kind === "Trifecta" || kind === "TriStraight") setBase((prev) => (prev === "1.00" || prev === "2.00" ? "0.50" : prev));
    // Reset input fields when kind changes so stale posts don't persist.
    setA("");
    setB("");
    setC("");
  }, [kind]);

  const postsA = parsePosts(a);
  const postsB = parsePosts(b);
  const postsC = parsePosts(c);
  const key = postsA[0] ?? null;
  const withPosts = postsB; // Trifecta "with" uses second input

  let combos = 0;
  let valid = false;
  let ticketBuilder = null;
  if (kind === "DD") {
    combos = postsA.length * postsB.length;
    valid = postsA.length >= 1 && postsB.length >= 1;
    ticketBuilder = () => ({
      kind: "DD",
      structure: `${postsA.length}x${postsB.length}`,
      horses: [
        { label: "1st Leg (DDPO)", posts: postsA },
        { label: "2nd Leg (SLPO)", posts: postsB },
      ],
    });
  } else if (kind === "Exacta") {
    combos = countExactaPairs(postsA, postsB);
    valid = postsA.length >= 1 && postsB.length >= 1 && combos > 0;
    ticketBuilder = () => ({
      kind: "Exacta",
      horses: [
        { label: "1st Position", posts: postsA },
        { label: "2nd Position", posts: postsB },
      ],
    });
  } else if (kind === "TriStraight") {
    combos = countTrifectaStraight(postsA, postsB, postsC);
    valid = combos > 0;
    ticketBuilder = () => ({
      kind: "Trifecta",
      shape: "straight",
      horses: [
        { label: "1st Position", posts: postsA },
        { label: "2nd Position", posts: postsB },
        { label: "3rd Position", posts: postsC },
      ],
    });
  } else {
    // Trifecta Key Box — a="key" (single post), b="with" (list)
    combos = withPosts.length >= 2 ? 3 * withPosts.length * (withPosts.length - 1) : 0;
    valid = key !== null && withPosts.length >= 2 && !withPosts.includes(key);
    ticketBuilder = () => ({
      kind: "Trifecta",
      shape: "keybox",
      key,
      with_posts: withPosts,
      horses: [
        { label: "Key", posts: [key] },
        { label: "With (any 2 of)", posts: withPosts },
        { label: "Any 3 of (key + with)", posts: [key, ...withPosts] },
      ],
    });
  }

  const baseNum = Number(base) || 0;
  const cost = (combos * baseNum).toFixed(2);
  const selectedTrack = tracks.find((t) => t.slug === trackSlug);
  const trackName = selectedTrack?.name || trackSlug || "";

  const submit = () => {
    if (!valid) {
      toast.error("Fill in posts correctly for this ticket type.");
      return;
    }
    if (!race) {
      toast.error("Race number is required.");
      return;
    }
    if (!baseNum || baseNum <= 0) {
      toast.error("Base must be greater than 0.");
      return;
    }
    const payload = {
      ...ticketBuilder(),
      track: trackName,
      track_slug: trackSlug,
      race: String(race),
      date,
      base: baseNum,
      combos,
      cost,
      manual: true,
    };
    const entry = saveTicket(payload);
    toast.success(
      `Manual ${payload.kind} added — ${trackName || "unknown track"} · Race ${race} · $${cost}`,
      {
        duration: 6500,
        action:
          onScoreTicket && entry?.track_slug && entry?.race && entry?.date
            ? {
                label: "Score now",
                onClick: () => onScoreTicket(entry),
              }
            : undefined,
      }
    );
    setA("");
    setB("");
    setC("");
    setRace("");
    onClose?.();
  };

  const inputCls =
    "px-2 h-8 rounded bg-[#0a0a0a] text-stone-100 text-xs border border-stone-700 focus:border-[#84cc16] focus:outline-none placeholder-stone-600";
  const labelCls =
    "block text-[10px] uppercase tracking-[0.2em] text-stone-500 mb-1";
  const monoStyle = {
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  };

  const fieldA =
    kind === "DD"
      ? { label: "Leg 1 Posts", ph: "e.g. 1,2" }
      : kind === "Exacta"
        ? { label: "Front Posts", ph: "e.g. 1,2,3" }
        : kind === "TriStraight"
          ? { label: "1st Position", ph: "e.g. 1,2" }
          : { label: "Key Post", ph: "e.g. 4" };
  const fieldB =
    kind === "DD"
      ? { label: "Leg 2 Posts", ph: "e.g. 3,5,7" }
      : kind === "Exacta"
        ? { label: "Back Posts", ph: "e.g. 4,5,6" }
        : kind === "TriStraight"
          ? { label: "2nd Position", ph: "e.g. 1,2,3" }
          : { label: "With Posts", ph: "e.g. 1,2,8,9,10" };

  return (
    <div
      data-testid="manual-ticket-form"
      className="px-5 py-4 border-b border-stone-800 bg-[#0e0e0e]"
    >
      <div className="grid gap-3 md:grid-cols-6">
        <div>
          <label className={labelCls} style={monoStyle}>Type</label>
          <select
            data-testid="manual-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className={inputCls}
            style={{ ...monoStyle, width: "100%" }}
          >
            <option value="DD">Daily Double</option>
            <option value="Exacta">Exacta</option>
            <option value="Trifecta">Trifecta Key Box</option>
            <option value="TriStraight">Trifecta Straight (1st / 2nd / 3rd)</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls} style={monoStyle}>Track</label>
          <select
            data-testid="manual-track"
            value={trackSlug}
            onChange={(e) => setTrackSlug(e.target.value)}
            className={inputCls}
            style={{ ...monoStyle, width: "100%" }}
          >
            {tracks.length === 0 && <option value="">—</option>}
            {tracks.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} style={monoStyle}>Race</label>
          <input
            data-testid="manual-race"
            value={race}
            onChange={(e) => setRace(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="7"
            className={inputCls}
            style={{ ...monoStyle, width: "100%" }}
          />
        </div>
        <div>
          <label className={labelCls} style={monoStyle}>Date</label>
          <input
            data-testid="manual-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputCls}
            style={{ ...monoStyle, width: "100%" }}
          />
        </div>
        <div>
          <label className={labelCls} style={monoStyle}>Base $</label>
          <input
            data-testid="manual-base"
            value={base}
            onChange={(e) => setBase(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="0.50"
            className={inputCls}
            style={{ ...monoStyle, width: "100%" }}
          />
        </div>
      </div>

      <div className={`grid gap-3 mt-3 ${kind === "TriStraight" ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        <PostsField label={fieldA.label} ph={fieldA.ph} value={a} onChange={setA} posts={postsA} testId="manual-posts-a" />
        <PostsField label={fieldB.label} ph={fieldB.ph} value={b} onChange={setB} posts={postsB} testId="manual-posts-b" />
        {kind === "TriStraight" && (
          <PostsField label="3rd Position" ph="e.g. 1,2,3,4" value={c} onChange={setC} posts={postsC} testId="manual-posts-c" />
        )}
      </div>

      <div
        className="flex items-center justify-between gap-3 mt-4 flex-wrap"
        style={monoStyle}
      >
        <div className="text-[11px] uppercase tracking-[0.18em]" data-testid="manual-summary">
          <span className="text-stone-500">Combos: </span>
          <span className="text-[#f5ecd6] font-bold">{combos}</span>
          <span className="text-stone-700 mx-2">|</span>
          <span className="text-stone-500">Cost: </span>
          <span className="text-[#84cc16] font-bold">${cost}</span>
          {kind === "Trifecta" && withPosts.includes(key) && (
            <span className="ml-3 text-[#fca5a5]">Key cannot be in With list.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="manual-cancel"
            onClick={onClose}
            className="px-3 h-8 rounded text-[11px] uppercase tracking-[0.18em] text-stone-400 hover:text-stone-200 border border-stone-700"
            style={monoStyle}
          >
            Cancel
          </button>
          <button
            data-testid="manual-submit"
            onClick={submit}
            disabled={!valid}
            className="inline-flex items-center gap-1 px-3 h-8 rounded text-[11px] font-bold uppercase tracking-[0.18em] bg-[#84cc16] text-[#0a0a0a] border border-[#365314] hover:bg-[#a3e635] disabled:opacity-40 disabled:cursor-not-allowed"
            style={monoStyle}
          >
            <Plus size={12} /> Add Ticket
          </button>
        </div>
      </div>
    </div>
  );
};

export const TicketHistoryPanel = ({ apiBase }) => {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [scoringId, setScoringId] = useState(null);
  const [scoringAll, setScoringAll] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tracks, setTracks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${apiBase}/tracks`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (Array.isArray(data.tracks)) setTracks(data.tracks);
      } catch {
        /* ignore */
      }
    })();
  }, [apiBase]);

  useEffect(() => {
    setTickets(getTickets());
    const refresh = () => setTickets(getTickets());
    window.addEventListener("storage", refresh);
    const unsub = onTicketHistoryChange(refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      unsub();
    };
  }, []);

  const onDelete = (id) => setTickets(deleteTicket(id));
  const onClear = () => {
    if (!window.confirm("Clear all ticket history?")) return;
    clearTickets();
    setTickets([]);
  };

  // Fetch one race's WPS.
  const fetchOneRace = useCallback(
    async ({ slug, race, date }) => {
      const url = `${apiBase}/fetch-race-results?track=${encodeURIComponent(
        slug
      )}&race=${race}&date=${encodeURIComponent(date)}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        const b = await resp.json().catch(() => ({}));
        throw new Error(b?.detail || `HTTP ${resp.status}`);
      }
      return resp.json();
    },
    [apiBase]
  );

  const scoreSingle = useCallback(
    async (ticket) => {
      if (scoringId || scoringAll) return;
      setScoringId(ticket.id);
      const toastId = toast.loading("Fetching race results…");
      try {
        const race = Number(ticket.race);
        const racesNeeded =
          ticket.kind === "DD" ? [race, race + 1] : [race];
        const results = {};
        for (const r of racesNeeded) {
          try {
            const resp = await fetchOneRace({
              slug: ticket.track_slug,
              race: r,
              date: ticket.date,
            });
            results[r] = resp;
          } catch {
            /* leave undefined; scoreTicket returns null on missing race */
          }
        }
        const score = scoreTicket(ticket, results);
        if (!score) {
          toast.warning(
            "Results not published yet — the race may not be official.",
            { id: toastId, duration: 6000 }
          );
          return;
        }
        // If the backend had to shift the date to find the race, persist
        // the corrected date on the ticket so later lookups go straight
        // there and the Performance Report groups it on the right day.
        const firstResp = results[racesNeeded[0]];
        const corrected = firstResp?.date_adjusted_from
          ? { date: firstResp.date }
          : {};
        updateTicket(ticket.id, { score, ...corrected });
        toast[score.hit ? "success" : "error"](
          `Ticket scored: ${score.hit ? "HIT" : "MISS"}${
            firstResp?.date_adjusted_from
              ? ` (date auto-fixed to ${firstResp.date})`
              : ""
          }`,
          { id: toastId, duration: 4500 }
        );
      } catch (err) {
        toast.error(String(err.message || err), {
          id: toastId,
          duration: 8000,
        });
      } finally {
        setScoringId(null);
      }
    },
    [scoringId, scoringAll, fetchOneRace]
  );

  const scoreAll = useCallback(async () => {
    if (scoringAll || scoringId) return;
    const unscored = tickets.filter(
      (t) => !t.score && t.track_slug && t.race && t.date
    );
    if (unscored.length === 0) {
      toast.info("No unscored tickets to score.");
      return;
    }
    setScoringAll(true);
    const toastId = toast.loading(`Scoring ${unscored.length} tickets…`);
    try {
      // Cache each fetched race by (slug, race, date) so duplicate tickets on
      // the same race hit the network only once.
      const raceCache = {};
      let hits = 0;
      let scored = 0;
      let pending = 0;
      for (const t of unscored) {
        const race = Number(t.race);
        const racesNeeded = t.kind === "DD" ? [race, race + 1] : [race];
        const tRes = {};
        for (const r of racesNeeded) {
          const key = `${t.track_slug}|${r}|${t.date}`;
          if (!(key in raceCache)) {
            try {
              raceCache[key] = await fetchOneRace({
                slug: t.track_slug,
                race: r,
                date: t.date,
              });
            } catch {
              raceCache[key] = null;
            }
          }
          if (raceCache[key]) tRes[r] = raceCache[key];
        }
        const s = scoreTicket(t, tRes);
        if (!s) {
          pending += 1;
          continue;
        }
        updateTicket(t.id, { score: s });
        scored += 1;
        if (s.hit) hits += 1;
      }
      toast.success(
        `Scored ${scored} of ${unscored.length} · ${hits} HIT${
          hits === 1 ? "" : "S"
        }${pending ? ` · ${pending} still pending` : ""}`,
        { id: toastId, duration: 6000 }
      );
    } catch (err) {
      toast.error(String(err.message || err), {
        id: toastId,
        duration: 8000,
      });
    } finally {
      setScoringAll(false);
    }
  }, [scoringAll, scoringId, tickets, fetchOneRace]);

  const scoredCount = tickets.filter((t) => t.score).length;
  const hitCount = tickets.filter((t) => t.score?.hit).length;
  const netTotal = tickets.reduce(
    (acc, t) => (t.score && t.score.net != null ? acc + t.score.net : acc),
    0
  );

  return (
    <section
      className="mt-8 mx-auto max-w-3xl rounded-lg ring-1 ring-stone-700/50"
      style={{ background: "#141414" }}
      data-testid="ticket-history"
    >
      <header
        data-testid="ticket-history-toggle"
        className="px-5 py-3 border-b border-stone-800 flex items-center justify-between cursor-pointer select-none"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <History size={16} className="text-[#84cc16]" />
          <h3
            className="text-xl text-[#f5ecd6]"
            style={{
              fontFamily: "'Instrument Serif', 'Times New Roman', serif",
              fontWeight: 400,
            }}
          >
            Ticket History
          </h3>
          <PanelHelp step={7} label="Ticket History" />
          <span
            className="text-[11px] uppercase tracking-[0.2em] text-stone-500"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            {tickets.length} saved
          </span>
          {scoredCount > 0 && (
            <span
              className="text-[11px] uppercase tracking-[0.2em] text-[#84cc16]"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              data-testid="hist-hit-rate"
            >
              {hitCount}/{scoredCount} hit
              <span
                data-testid="hist-net-total"
                className="ml-3"
                style={{
                  color: netTotal >= 0 ? "#84cc16" : "#fca5a5",
                }}
              >
                Net {netTotal >= 0 ? "+" : "-"}$
                {Math.abs(netTotal).toFixed(2)}
              </span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {open && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAdding((v) => !v);
              }}
              className={`inline-flex items-center gap-1 px-2 h-7 rounded text-[10px] uppercase tracking-[0.18em] border transition ${
                adding
                  ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                  : "text-[#f5ecd6] border-stone-700 hover:border-[#f5ecd6]"
              }`}
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              data-testid="hist-manual-add-toggle"
              title="Add a ticket manually (bets placed outside the handicap tool)"
            >
              <Plus size={12} /> {adding ? "Close" : "Add manually"}
            </button>
          )}
          {tickets.length > 0 && open && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  scoreAll();
                }}
                disabled={scoringAll}
                className="inline-flex items-center gap-1 px-2 h-7 rounded text-[10px] uppercase tracking-[0.18em] text-[#84cc16] border border-[#365314] hover:bg-[#84cc16] hover:text-[#0a0a0a] transition disabled:opacity-40"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                data-testid="hist-score-all"
              >
                {scoringAll ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Trophy size={12} />
                )}
                Score all
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-stone-500 hover:text-red-400 transition"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                data-testid="hist-clear-all"
              >
                <Trash2 size={12} /> Clear all
              </button>
            </>
          )}
          <span
            className="text-[11px] text-stone-400"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          >
            {open ? "Hide ▲" : "Show ▼"}
          </span>
        </div>
      </header>

      {open && (
        <div data-testid="hist-list">
          {adding && (
            <ManualTicketForm
              tracks={tracks}
              onClose={() => setAdding(false)}
              onScoreTicket={scoreSingle}
            />
          )}
          {tickets.length === 0 ? (
            <div
              className="px-6 py-8 text-stone-400 text-sm"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              }}
            >
              No tickets yet. Archive a ticket from the DD / Exacta /
              Trifecta cards and it will appear here.
            </div>
          ) : (
            tickets.map((t) => (
              <TicketRow
                key={t.id}
                t={t}
                onDelete={onDelete}
                onScore={scoreSingle}
                scoring={scoringId === t.id}
                tracks={tracks}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
};
